import type { Shell as ShellData } from '@oasis/contracts';
import { useQuery } from '@tanstack/react-query';
import {
  Briefcase,
  ChevronDown,
  ChevronRight,
  Settings,
  UserRound,
  WifiOff,
  X,
} from 'lucide-react';
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { Link, Outlet, useLocation } from 'react-router';

import { useApi } from '../api.js';
import { useSession } from '../auth.js';
import logo from '../design/assets/logo-apc34.png';
import {
  Breadcrumb,
  ErrorMessage,
  Loading,
  PageContext,
  PageTabs,
  readTrail,
  type Crumb,
} from '../design/components.js';

import { HeaderSearch } from './GlobalSearch.js';
import { iconOf } from './icons.js';
import {
  ACCUEIL_BENEVOLE,
  ACCUEIL_GESTION,
  ADMINISTRATION,
  benevoleBar,
  bottomBar,
  cartesPath,
  chromeOf,
  COMPTE,
  entreeDe,
  espaceAt,
  gestionRubriques,
  hasAdministration,
  hubAt,
  locate,
  rubriquesEnTete,
  type BarItem,
  type DomaineInfo,
  type Espace,
  type Location,
} from './navigation.js';
import { useMesRubriques } from './rubriques.js';

/**
 * Coque de l'interface en deux espaces (révision du 2026-10-31, docs/ux/navigation.md) :
 * - espace bénévole = l'application : Accueil · Dispos · Activités · Live · Plus, en haut sur
 *   ordinateur et en barre du bas sur téléphone ;
 * - espace gestion = l'intranet et le bloc « Gestion » de l'app : Opérations, Logistique,
 *   Bénévoles, Communication, Commandement, Support ;
 * - un seul bouton pour passer de l'un à l'autre, montré seulement à qui a des écrans de
 *   gestion ; l'administration reste derrière l'engrenage.
 * Navigation, actions et tuiles viennent de `/ui/shell?view=tout`, calculées par le serveur ;
 * l'interface ne décide d'aucun droit, elle range seulement.
 */

interface ShellState {
  shell: ShellData;
  /** Espace de la page courante et en-tête affiché. */
  espace: Espace;
  chrome: 'benevole' | 'gestion';
  /** Rubriques de gestion permises (dans l'ordre). */
  gestion: DomaineInfo[];
  admin: boolean;
}

const ShellContext = createContext<ShellState | null>(null);

export function useShell(): ShellState {
  const state = useContext(ShellContext);
  if (state === null) throw new Error('Coque absente');
  return state;
}

const SHELL_PATH = '/ui/shell?view=tout';

function useOnline(): boolean {
  const [online, setOnline] = useState(() => navigator.onLine);
  useEffect(() => {
    const up = (): void => {
      setOnline(true);
    };
    const down = (): void => {
      setOnline(false);
    };
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, []);
  return online;
}

/** Rubrique de la page courante (page de rubrique, ou écran rangé dans une rubrique). */
function currentDomain(here: Location | undefined, pathname: string): string | undefined {
  if (pathname === ACCUEIL_BENEVOLE) return 'accueil';
  if (pathname === ACCUEIL_GESTION) return 'gestion';
  return hubAt(pathname)?.key ?? here?.domaine.key;
}

/** Fil d'Ariane et onglets de l'écran courant, au-dessus de la page. */
function PageNavigation({
  here,
  pathname,
}: {
  here: Location | undefined;
  pathname: string;
}): ReactElement | null {
  const state: unknown = useLocation().state;
  const { shell } = useShell();
  if (here === undefined || hubAt(pathname) !== undefined) return null;
  // Page d'entrée de sa rubrique (module.json `entree`) : lien vers tous les écrans de la
  // rubrique, puisque la rubrique s'ouvre directement ici.
  const entree = entreeDe(here.domaine.key, shell.navigation);
  const toutes =
    here.exact && entree?.path === pathname ? (
      <Link className="o-page-nav__all" to={cartesPath(here.domaine, shell.navigation)}>
        Tous les écrans de la rubrique {here.domaine.label}
        <ChevronRight aria-hidden="true" size={16} />
      </Link>
    ) : null;
  const trail = readTrail(state);
  // Fiche ouverte depuis une autre : le chemin d'arrivée remplace la rubrique.
  const crumbs: Crumb[] =
    trail.length > 0 && !here.exact
      ? [
          ...trail.map((t, i) => ({
            label: t.label,
            to: t.to,
            state: { trail: trail.slice(0, i) },
          })),
          ...here.crumbs.slice(-1),
        ]
      : here.crumbs;
  const tabs =
    here.exact && here.item.tabs.length > 1
      ? here.item.tabs.map((t) => ({ to: t.path, label: t.label, icon: iconOf(t.icon) }))
      : [];
  if (crumbs.length <= 1 && tabs.length === 0 && toutes === null) return null;
  return (
    <div className="o-page-nav">
      <div className="o-page-nav__top">
        <Breadcrumb items={crumbs} />
        {toutes}
      </div>
      <PageTabs tabs={tabs} label={here.item.label} />
    </div>
  );
}

/**
 * Bouton unique de passage d'un espace à l'autre (comme le bloc « Gestion » de l'app) :
 * absent pour qui n'a aucun écran de gestion (droits calculés par le serveur). Sur ordinateur
 * et tablette il est dans l'en-tête ; au téléphone, où l'en-tête n'a plus de place, il est en
 * tête de « Plus » (variante `plus`), comme le bloc « Gestion » de l'onglet Plus de l'app : une
 * seule des deux variantes est affichée à une largeur donnée.
 */
export function SpaceSwitch({
  variant = 'entete',
  onClick,
}: {
  variant?: 'entete' | 'plus';
  onClick?: () => void;
}): ReactElement | null {
  const { chrome, gestion } = useShell();
  if (gestion.length === 0) return null;
  const toGestion = chrome === 'benevole';
  const Icon = toGestion ? Briefcase : UserRound;
  const label = toGestion ? 'Espace gestion' : 'Espace bénévole';
  const to = toGestion ? ACCUEIL_GESTION : ACCUEIL_BENEVOLE;
  if (variant === 'plus')
    return (
      <Link to={to} className="o-space-switch" onClick={onClick}>
        <Icon aria-hidden="true" size={22} />
        <span className="o-space-switch__text">
          {label}
          <span className="o-space-switch__hint">
            {toGestion
              ? 'Opérations, logistique, bénévoles, communication, commandement, support.'
              : 'Accueil, dispos, activités, Live et Plus.'}
          </span>
        </span>
        <ChevronRight aria-hidden="true" size={18} />
      </Link>
    );
  return (
    <Link to={to} className="o-header__switch">
      <Icon aria-hidden="true" size={18} />
      <span className="o-header__switch-label">{label}</span>
    </Link>
  );
}

/**
 * Administration au téléphone (variante « Plus ») : l'en-tête n'a pas la place de l'engrenage à
 * cette largeur (défilement horizontal sinon) ; l'accès passe en tête de « Plus », sous le
 * bouton d'espace. Absent sans écran d'administration (droits calculés par le serveur).
 */
export function AdministrationPlus({ onClick }: { onClick?: () => void }): ReactElement | null {
  const { admin } = useShell();
  if (!admin) return null;
  return (
    <Link to={ADMINISTRATION.path} className="o-space-switch" onClick={onClick}>
      <Settings aria-hidden="true" size={22} />
      <span className="o-space-switch__text">
        {ADMINISTRATION.label}
        <span className="o-space-switch__hint">{ADMINISTRATION.phrase}</span>
      </span>
      <ChevronRight aria-hidden="true" size={18} />
    </Link>
  );
}

/** « Plus » de l'espace gestion au téléphone : toutes les rubriques et l'administration. */
function MorePanel({
  onClose,
  current,
}: {
  onClose: () => void;
  current: string | undefined;
}): ReactElement {
  const { gestion, admin } = useShell();
  const groups: { title: string; items: DomaineInfo[] }[] = [
    { title: 'Rubriques de gestion', items: gestion },
    { title: 'Mon compte', items: [COMPTE, ...(admin ? [ADMINISTRATION] : [])] },
  ];
  useEffect(() => {
    const close = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', close);
    return () => {
      window.removeEventListener('keydown', close);
    };
  }, [onClose]);
  return (
    <div className="o-more" role="dialog" aria-modal="true" aria-labelledby="o-more-title">
      <div className="o-more__head">
        <h2 id="o-more-title">Plus</h2>
        <button
          type="button"
          className="o-button o-button--ghost o-icon-button"
          aria-label="Fermer"
          onClick={onClose}
        >
          <X aria-hidden="true" />
        </button>
      </div>
      <SpaceSwitch variant="plus" onClick={onClose} />
      {groups.map((g) =>
        g.items.length === 0 ? null : (
          <nav key={g.title} aria-label={g.title}>
            <h3 className="o-more__section">{g.title}</h3>
            <ul className="o-more__list">
              {g.items.map((d) => {
                const Icon = iconOf(d.icon);
                return (
                  <li key={d.key}>
                    <Link
                      to={d.path}
                      onClick={onClose}
                      aria-current={current === d.key ? 'true' : undefined}
                    >
                      <Icon aria-hidden="true" size={20} />
                      <span className="o-more__label">
                        {d.label}
                        <span className="o-more__phrase">{d.phrase}</span>
                      </span>
                      <ChevronRight aria-hidden="true" size={18} className="o-more__chevron" />
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>
        ),
      )}
    </div>
  );
}

function BottomBar({
  items,
  current,
  onMore,
}: {
  items: readonly BarItem[];
  current: string | undefined;
  onMore: () => void;
}): ReactElement {
  return (
    <nav className="o-bottom-bar" aria-label="Navigation rapide">
      {items.map((item) => {
        const Icon = iconOf(item.icon);
        if (item.path === '')
          return (
            <button key={item.key} type="button" className="o-bottom-bar__item" onClick={onMore}>
              <Icon aria-hidden="true" size={22} />
              <span>{item.label}</span>
            </button>
          );
        return (
          <Link
            key={item.key}
            to={item.path}
            className="o-bottom-bar__item"
            aria-current={item.key === current ? 'page' : undefined}
          >
            <Icon aria-hidden="true" size={22} />
            <span>{item.label}</span>
          </Link>
        );
      })}
    </nav>
  );
}

export function AppShell({ notifications }: { notifications?: ReactNode }): ReactElement {
  const { config, me } = useSession();
  const call = useApi();
  const online = useOnline();
  const location = useLocation();
  const [more, setMore] = useState(false);
  const [autresOuvert, setAutresOuvert] = useState(false);
  const { epinglees } = useMesRubriques();
  const shellQuery = useQuery<ShellData>({
    queryKey: [SHELL_PATH],
    queryFn: () => call<ShellData>(SHELL_PATH),
  });

  useEffect(() => {
    setMore(false);
    setAutresOuvert(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!autresOuvert) return undefined;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setAutresOuvert(false);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [autresOuvert]);

  useEffect(() => {
    document.title = config.mode === 'preproduction' ? '[PRÉPROD] Oasis' : 'Oasis';
  }, [config.mode]);

  const data = shellQuery.data;
  const navigation = useMemo(() => data?.navigation ?? [], [data?.navigation]);
  const here = useMemo(
    () => locate(navigation, location.pathname),
    [navigation, location.pathname],
  );
  const gestion = useMemo(() => gestionRubriques(navigation), [navigation]);

  if (shellQuery.isError && data === undefined) return <ErrorMessage error={shellQuery.error} />;
  if (data === undefined) return <Loading />;

  const admin = hasAdministration(data.navigation);
  const espace = espaceAt(data.navigation, location.pathname);
  const chrome = chromeOf(espace, data.navigation);
  const state: ShellState = { shell: data, espace, chrome, gestion, admin };
  const current = currentDomain(here, location.pathname);
  const name = me.display_name ?? me.username;
  // En-tête de l'espace : entrées de l'app (bénévole) ou rubriques de gestion.
  const enTete = rubriquesEnTete(gestion, data.navigation, epinglees);
  const top: BarItem[] =
    chrome === 'benevole'
      ? benevoleBar(data.navigation)
      : enTete.visibles.map((d) => ({ key: d.key, label: d.label, path: d.path, icon: d.icon }));
  const autres = chrome === 'benevole' ? [] : enTete.autres;
  const bar = bottomBar(data.navigation, chrome);
  const home = chrome === 'gestion' ? ACCUEIL_GESTION : ACCUEIL_BENEVOLE;
  const barCurrent = current === 'accueil' ? 'accueil' : current === 'compte' ? 'plus' : current;

  return (
    <ShellContext.Provider value={state}>
      <a href="#contenu" className="o-skip-link">
        Aller au contenu
      </a>
      {config.mode === 'preproduction' && (
        <div role="status" className="o-banner o-banner--preprod">
          PRÉPRODUCTION — aucune communication réelle ne sort de cet environnement
        </div>
      )}
      {!online && (
        <div role="status" className="o-banner o-banner--offline">
          <WifiOff aria-hidden="true" size={16} /> Hors connexion : les dernières pages consultées
          restent affichées, aucune saisie ne sera envoyée tant que la connexion n'est pas revenue.
        </div>
      )}
      <header className={`o-header o-header--${chrome}`}>
        {/* Nom accessible tiré du contenu, sans aria-label (WCAG 2.5.3, axe
            label-content-name-mismatch : le texte visible « Oasis » + « Gestion » doit figurer
            dans le nom, et axe les colle sans espace) : le logo porte « Oasis » (le mot voisin,
            masqué en tablette, est décoratif), puis le repère d'espace et la destination, lue
            seulement. */}
        <Link to={home} className="o-header__brand">
          <img src={logo} alt="Oasis" height={36} />
          <span className="o-header__wordmark" aria-hidden="true">
            Oasis
          </span>
          {chrome === 'gestion' && <span className="o-header__space">Gestion</span>}
          <span className="o-visually-hidden">
            {chrome === 'gestion' ? ' — accueil de la gestion' : ' — accueil'}
          </span>
        </Link>
        <nav
          className="o-domains"
          aria-label={chrome === 'gestion' ? 'Rubriques de gestion' : 'Rubriques'}
        >
          {top.map((d) => {
            const Icon = iconOf(d.icon);
            return (
              <Link
                key={d.key}
                to={d.path}
                className="o-domains__link"
                aria-current={
                  current === d.key || (d.key === 'plus' && current === 'compte')
                    ? location.pathname === d.path
                      ? 'page'
                      : 'true'
                    : undefined
                }
              >
                <Icon aria-hidden="true" size={16} />
                {d.label}
              </Link>
            );
          })}
          {autres.length > 0 && (
            <div className="o-domains__more">
              <button
                type="button"
                className="o-domains__link"
                aria-expanded={autresOuvert}
                aria-controls="rubriques-autres"
                aria-current={autres.some((d) => d.key === current) ? 'true' : undefined}
                onClick={() => {
                  setAutresOuvert((o) => !o);
                }}
              >
                Autres
                <ChevronDown aria-hidden="true" size={14} className="o-domains__chevron" />
              </button>
              {autresOuvert && (
                <ul id="rubriques-autres" className="o-domains__menu">
                  {autres.map((d) => (
                    <li key={d.key}>
                      <Link
                        to={d.path}
                        className="o-domains__menu-link"
                        aria-current={current === d.key ? 'true' : undefined}
                      >
                        {d.label}
                      </Link>
                    </li>
                  ))}
                  <li>
                    <Link
                      to={COMPTE.path}
                      className="o-domains__menu-link o-domains__menu-link--hint"
                    >
                      Choisir mes rubriques…
                    </Link>
                  </li>
                </ul>
              )}
            </div>
          )}
        </nav>
        <div className="o-header__actions">
          <HeaderSearch navigation={data.navigation} actions={data.actions} espace={chrome} />
          <SpaceSwitch />
          {notifications}
          {admin && (
            <Link
              to={ADMINISTRATION.path}
              className="o-header__gear"
              aria-label="Administration"
              title="Administration"
              aria-current={current === 'administration' ? 'true' : undefined}
            >
              <Settings aria-hidden="true" size={20} />
            </Link>
          )}
          <Link
            to={COMPTE.path}
            className="o-header__avatar"
            title="Mon compte"
            aria-current={current === 'compte' ? 'true' : undefined}
          >
            {/* L'initiale est décorative (aria-hidden) ; le nom accessible vient du texte
                masqué, sans aria-label qui le contredirait (axe label-content-name-mismatch). */}
            <span className="o-avatar" aria-hidden="true">
              {name.slice(0, 1).toUpperCase()}
            </span>
            <span className="o-visually-hidden">Mon compte ({name})</span>
          </Link>
        </div>
      </header>
      <div className="o-layout">
        <main id="contenu" className="o-main" tabIndex={-1}>
          <PageNavigation here={here} pathname={location.pathname} />
          <PageContext.Provider value={{ section: here?.section, trail: here !== undefined }}>
            <Outlet />
          </PageContext.Provider>
        </main>
      </div>
      <BottomBar
        items={bar}
        current={barCurrent}
        onMore={() => {
          setMore(true);
        }}
      />
      {more && (
        <MorePanel
          current={current}
          onClose={() => {
            setMore(false);
          }}
        />
      )}
    </ShellContext.Provider>
  );
}
